/**
 * Nuxt-compatibility shim that lets the admin render the REAL public-site block
 * components (frontend/components/blocks/*) inside the builder's live canvas, so
 * the preview is the same code — not a copy — as the published page.
 *
 * Those SFCs lean on two things Nuxt provides for free that a plain Vite app does
 * not:
 *   1. Auto-imported Vue APIs (`ref`, `computed`, …) — used as bare globals with
 *      no import statement.
 *   2. Nuxt composables (`useI18n`, `useLocalePath`, `useHead`) — likewise bare.
 *
 * Vite has no auto-import, so we publish both on `globalThis`: a bare `computed(…)`
 * in an imported component resolves up the scope chain to `globalThis.computed`.
 * A component that DOES import a name locally just shadows the global, so this can
 * never clash with the admin's own (always-explicit) imports. Importing this
 * module for its side effects (once, from main.js) is the entire adapter — no new
 * dependency, no build plugin.
 */
import * as Vue from "vue";

import zhHant from "~/i18n/locales/zh-Hant.json";
import zhHans from "~/i18n/locales/zh-Hans.json";
import en from "~/i18n/locales/en.json";

// The real site message catalogues, so preview labels ("了解更多", "查看所有活動",
// the empty-state copy, …) read exactly as they do live.
const MESSAGES = { "zh-Hant": zhHant, "zh-Hans": zhHans, en };

// Active locale for the canvas, driven by LiveCanvas's locale tab. A real Vue ref
// so `t()` is reactive — switching the locale tab re-renders the preview strings.
const currentLocale = Vue.ref("zh-Hant");

/** Point the preview's i18n at a locale (matches the editor's active locale). */
export function setPreviewLocale(locale) {
  if (locale && MESSAGES[locale]) currentLocale.value = locale;
}

function lookup(messages, key) {
  return key.split(".").reduce((o, k) => (o == null ? undefined : o[k]), messages);
}

/** vue-i18n-style `t`: dotted key lookup + `{param}` interpolation, falling back
 *  to the default locale then the key itself. */
function translate(key, params) {
  const loc = currentLocale.value;
  let val = lookup(MESSAGES[loc] || MESSAGES["zh-Hant"], key);
  if (val == null) val = lookup(MESSAGES["zh-Hant"], key);
  if (val == null) return key;
  if (params && typeof val === "string") {
    val = val.replace(/\{(\w+)\}/g, (m, p) => (params[p] != null ? String(params[p]) : m));
  }
  return val;
}

// The site locales (code + display name), matching nuxt.config i18n.locales —
// used by the real LanguageSwitcher rendered in the preview chrome.
const locales = Vue.ref([
  { code: "zh-Hant", name: "繁體中文" },
  { code: "zh-Hans", name: "简体中文" },
  { code: "en", name: "English" },
]);

// ---- Nuxt composable shims the public-site components call as auto-imports ----
export function useI18n() {
  // `locale`/`locales`/`setLocale` are what LanguageSwitcher reads; switching just
  // repoints the preview locale (the editor's locale tab is the real control).
  return { t: translate, locale: currentLocale, locales, setLocale: setPreviewLocale };
}
// Links never navigate in the preview (LiveCanvas swallows clicks), so the locale
// path prefix is irrelevant — identity keeps hrefs readable.
export function useLocalePath() {
  return (p) => p;
}
// SectionBlock injects compiled CSS via useHead; section trees render through a
// dedicated canvas path, so this is a harmless no-op here.
export function useHead() {}
export function useRoute() {
  return { query: {}, params: {}, path: "/", fullPath: "/" };
}
export function useRuntimeConfig() {
  return { public: {} };
}
export function useNuxtApp() {
  // Only `$i18n.locale.value` is read (by useCms.getSiteFooter); the footer editor
  // passes its data directly so that path isn't hit, but keep it valid regardless.
  return { $i18n: { locale: currentLocale } };
}
export function useState(_key, init) {
  return Vue.ref(typeof init === "function" ? init() : (init ?? null));
}
// SiteFooter loads its data via `await useAsyncData(key, handler, opts)`. There's no
// SSR cache here — just run the handler once and hand back a `{ data }` ref. The
// footer editor passes its live data through the handler, so this resolves instantly
// with no network. Accepts both call shapes: (key, handler, opts) and (handler, opts).
export async function useAsyncData(keyOrHandler, handlerOrOpts) {
  const handler = typeof handlerOrOpts === "function" ? handlerOrOpts : keyOrHandler;
  let result = null;
  try { if (typeof handler === "function") result = await handler(); } catch { result = null; }
  return {
    data: Vue.ref(result),
    pending: Vue.ref(false),
    error: Vue.ref(null),
    status: Vue.ref("success"),
    refresh: async () => {},
    execute: async () => {},
  };
}

// Publish Vue's setup APIs as globals so the imported SFCs' bare references
// resolve. Only assign when absent so we never stomp anything pre-existing.
const VUE_GLOBALS = [
  "ref", "computed", "reactive", "readonly", "shallowRef", "toRef", "toRefs",
  "unref", "isRef", "watch", "watchEffect", "nextTick",
  "onMounted", "onBeforeMount", "onBeforeUnmount", "onUnmounted", "onUpdated",
  "provide", "inject",
];
for (const name of VUE_GLOBALS) {
  if (Vue[name] && !globalThis[name]) globalThis[name] = Vue[name];
}
for (const [name, fn] of Object.entries({
  useI18n, useLocalePath, useHead, useRoute, useRuntimeConfig,
  useNuxtApp, useState, useAsyncData,
})) {
  if (!globalThis[name]) globalThis[name] = fn;
}
